// packages/main/app/web/components-v2/UnifiedImageDialog/styles.ts
import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	tabTriggerFreeImages: {
		width: '100%',
		whiteSpace: 'nowrap',
	},
	dialogContent: {
		maxWidth: '90vw',
		maxHeight: '90vh',
		width: '1200px',
		height: '700px',
		display: 'flex',
		flexDirection: 'column',
		padding: 0,
	},
	dialogHeader: {
		padding: `${THEME.spacing.lg}px ${THEME.spacing.lg}px ${THEME.spacing.md}px`,
	},
	dialogBody: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
		padding: 0,
	},
	tabsContainer: {
		display: 'flex',
		flex: 1,
		overflow: 'hidden',
	},
	verticalTabList: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		padding: THEME.spacing.md,
		borderRight: `1px solid ${THEME.colors.border.light}`,
		width: '180px',
		maxWidth: '20%',
		flexShrink: 0,
		// Target tab trigger buttons within this list
		':global(button)': {
			display: 'flex',
			alignItems: 'center',
			gap: THEME.spacing.md,
			padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
			border: 'none',
			borderRadius: THEME.common.borderRadius,
			backgroundColor: 'transparent',
			color: THEME.colors.text.main,
			fontSize: THEME.typography.fontSizes.sm,
			fontWeight: THEME.typography.fontWeights.standard,
			cursor: 'pointer',
			transition: 'all 0.2s',
			textAlign: 'left',
			width: '100%',
		},
		':global(button:hover)': {
			backgroundColor: THEME.colors.hover.light,
		},
		':global(button[data-state="active"])': {
			backgroundColor: THEME.colors.primary.light,
			color: THEME.colors.primary.main,
		},
		// Target tab icons to ensure consistent sizing and alignment
		':global(.tab-icon)': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			flexShrink: 0,
			color: 'inherit', // Inherit color from button
			lineHeight: 1, // Remove line-height to prevent vertical offset
		},
		':global(.tab-icon svg)': {
			display: 'block', // Remove inline spacing
			color: 'inherit', // Ensure SVG inherits color
		},
	},
	tabTrigger: {
		display: 'flex',
		alignItems: 'center',
	},
	tabContent: {
		flex: 1,
		overflow: 'auto',
		padding: `${THEME.spacing.lg}px`,
	},
	tabIcon: {
		width: '20px',
		height: '20px',
	},
	// Deliberately no borderTop: the AI tabs already draw one above their chat input, and a second rule
	// immediately below it reads as a stray double line.
	saveToLibraryBar: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
		flexShrink: 0,
	},
	saveToLibraryLabel: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.main,
		cursor: 'pointer',
		marginBottom: 0,
	},
	tabContentWrapper: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
	},
	uploadContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		height: '100%',
	},
	dropzone: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		border: `2px dashed ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		padding: THEME.spacing.xl,
		cursor: 'pointer',
		transition: 'all 0.2s',
		':hover': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.primary.light,
		},
		'[data-drag-active]': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.hover.light,
		},
	},
	dropzoneContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: THEME.spacing.md,
		textAlign: 'center',
	},
	dropzoneIcon: {
		width: 64,
		height: 64,
		color: THEME.colors.icon.gray,
	},
	dropzoneTitle: {
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	dropzoneDescription: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	fileInput: {
		display: 'none',
	},
	errorMessage: {
		padding: THEME.spacing.sm,
		backgroundColor: THEME.colors.danger.light,
		color: THEME.colors.danger.dark,
		borderRadius: THEME.common.borderRadius,
		fontSize: THEME.typography.fontSizes.sm,
	},
	chatContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		gap: THEME.spacing.md,
	},
	chatMessages: {
		flex: 1,
		overflow: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	chatInputContainer: {
		borderTop: `1px solid ${THEME.colors.border.light}`,
		paddingTop: THEME.spacing.md,
	},
	previewSection: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	previewImageContainer: {
		flex: 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: THEME.spacing.xl,
		backgroundColor: THEME.colors.background.light,
	},
	previewImage: {
		maxWidth: '100%',
		maxHeight: '100%',
		objectFit: 'contain',
		borderRadius: THEME.common.borderRadius,
	},
	previewActions: {
		display: 'flex',
		gap: THEME.spacing.md,
		padding: THEME.spacing.lg,
		borderTop: `1px solid ${THEME.colors.border.light}`,
		justifyContent: 'flex-end',
	},
	bulkRebrandingContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	imageContainer: {
		height: 'auto',
	},
	image: {
		width: 120,
		height: 120,
		borderRadius: THEME.common.borderRadius,
		objectFit: 'cover',
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	tooltipTrigger: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	providedImagesTab: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		height: '100%',
		overflowY: 'auto',
		padding: SPACE[24],
		boxSizing: 'border-box',
	},
	providedImagesTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	providedImagesEmpty: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		padding: SPACE[24],
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	providedImagesGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
		gap: SPACE[16],
	},
	providedImageCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: SPACE[4],
		height: 'auto',
		padding: SPACE[8],
		textAlign: 'left',
		whiteSpace: 'normal',
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		':hover:not(:disabled)': {
			borderColor: THEME.colors.primary.main,
			backgroundColor: THEME.colors.hover.light,
		},
	},
	providedImageFrame: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: 140,
		marginBottom: SPACE[4],
		overflow: 'hidden',
		borderRadius: THEME.common.borderRadiusSm,
		backgroundColor: THEME.colors.background.main,
	},
	providedImageBadge: {
		position: 'absolute',
		top: SPACE[8],
		left: SPACE[8],
		padding: `${SPACE[4]}px ${SPACE[8]}px`,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	providedImage: {
		maxWidth: '100%',
		maxHeight: '100%',
		objectFit: 'contain',
	},
	providedImageMissing: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	providedImageName: {
		...THEME.common.truncate,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	providedImageDetail: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
});
